// ─────────────────────────────────────────────
//  SignOutButton — leave the ACCOUNT, from any page's topbar.
//
//  Anesthesia Team Control (ATC)
//  Copyright © 2026 Gonzalo Martinez (gamc00@gmail.com). All rights reserved.
//  Proprietary and confidential. Not licensed for public use — see /LICENSE.
// ─────────────────────────────────────────────
//
//  This behaviour lived on BoardPage alone, so every other page was a one-way
//  street: a CRNA on My Rooms, a scheduler on Schedule, anyone on Setup or the
//  timeline had to navigate back to the board to get out. Extracted here rather
//  than pasted into eleven topbars, because the sandbox rule below is exactly
//  the kind of step a copy quietly drops.
//
//  ⚠️ SIGNING OUT OF A DEMO SESSION MUST LEAVE THE SANDBOX, and leaving the
//  sandbox must be a FULL DOCUMENT LOAD — `firebase/config.js` binds `db` once
//  at boot, so a client-side navigation would clear the flag while the app kept
//  writing to the local store. `exitSandboxOnSignOut` handles that and reports
//  whether it took over the navigation; see sandbox/exit.js for the trap it
//  closes (a demo session that outlived its sign-out and shadowed the real
//  hospital database).
//
//  ⚠️ NOT THE SAME ACTION as ProviderPage's "Sign Out of Room", which releases
//  the room and leaves you logged in. Two different consequences must not share
//  one verb — that page's control names the room, and its confirmation says so.
//
//  Icon-only on phones so a narrow topbar (which already carries the nav menu,
//  facility tag, alert controls and avatar) does not overflow.
// ─────────────────────────────────────────────
import React from "react";
import { useNavigate } from "react-router-dom";
import { logout } from "../firebase/auth";
import { exitSandboxOnSignOut } from "../sandbox/exit";
import { useIsMobile } from "../hooks/useIsMobile";
import tokens from "../theme/tokens";

const { color, font } = tokens;

export default function SignOutButton({ compact }) {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const iconOnly = compact === undefined ? isMobile : compact;

  const handleSignOut = async () => {
    await logout();
    // Returns true when it has taken over with a hard reload to /login.
    if (!exitSandboxOnSignOut()) navigate("/login");
  };

  return (
    <button
      type="button"
      style={styles.button}
      onClick={handleSignOut}
      title="Sign out"
      aria-label="Sign out"
    >
      {iconOnly ? "⏻" : "Sign out"}
    </button>
  );
}

const styles = {
  // Matches the ghost button this was extracted from, so the control people
  // already know on the board looks identical everywhere else.
  button: {
    background: "transparent",
    border: "none",
    color: color.text.muted,
    cursor: "pointer",
    fontSize: font.size.label,
    fontFamily: "inherit",
    whiteSpace: "nowrap",
    // ── THE HIT AREA, WHICH IS NOT THE LOOK ────────────────────────────────
    //
    // Measured in Chrome on the deployed app, 2026-08-20: **44 x 15**. A ghost
    // button with no padding is exactly the size of its own text, and 15px is
    // well under the 24px floor of WCAG 2.2 SC 2.5.8 Target Size (Minimum) —
    // on EVERY page, because ten topbars share this one component.
    //
    // ⚠️ PADDING PLUS AN EQUAL NEGATIVE MARGIN, NOT `minHeight`. Ten topbars
    // lay this control out and they are not all fixed-height: BoardPage's is
    // `height: 56` and would not have noticed, but a content-sized topbar would
    // have grown by 9px the moment the box did, and "the sign-out button moved
    // the header" is a regression on every screen at once. Padding grows the
    // pressable box; the negative margin hands the same pixels straight back,
    // so the MARGIN box — the only thing a flex row measures — is still 44 x 15
    // and no sibling shifts. The wrapper `<span style={{marginLeft:"auto"}}>`
    // several pages use shrink-wraps to that same margin box.
    boxSizing: "border-box",
    padding: "5px 4px",
    margin: "-5px -4px",
    // ⚠️ AND THE ICON-ONLY VARIANT IS THE WORSE CASE, NOT THE BETTER ONE. On a
    // phone this button is a single "⏻" glyph, roughly 13px wide, so it failed
    // BOTH axes while the desktop label only failed one. `min-width` is the
    // one dimension here that cannot be given back — 24px of border box less
    // 8px of cancelled margin leaves the icon 3px wider in the layout than it
    // was, which is the whole cost of the fix and only on phones.
    minWidth: 24,
  },
};
